import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc,
  query, orderBy, serverTimestamp, writeBatch
} from 'firebase/firestore';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSearch, FiPackage, FiCopy, FiStar
} from 'react-icons/fi';
import PageLayout from '../../components/PageLayout';
import AccountSelect from '../../components/AccountSelect';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import { colors, spacing, borderRadius, typography, shadows } from '../../theme/colors';
import usePagePermissions from '../../hooks/usePagePermissions';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import useEscapeClose from '../../hooks/useEscapeClose';

// ===== Constants =====
const CHARGE_TYPES = [
  { value: 'percentage', label: '%' },
  { value: 'amount', label: 'Amount' }
];

const BASE_OPTIONS = [
  { value: 'cif', label: 'CIF Value' },
  { value: 'cifPlusDuties', label: 'CIF + Prior Charges (cumulative)' },
  { value: 'manual', label: 'Manual / N/A' }
];

const ALLOCATION_METHODS = [
  { value: 'value', label: 'By Value' },
  { value: 'qty', label: 'By Quantity' },
  { value: 'weight', label: 'By Weight' },
  { value: 'volume', label: 'By Volume' },
  { value: 'manual', label: 'Manual' }
];

const EMPTY_CHARGE = {
  chargeName: '',
  type: 'percentage',
  value: 0,
  base: 'cif',
  // Payable / Credit side — always required (the liability for this charge)
  accountId: '',
  accountCode: '',
  accountName: '',
  // Claim / Debit side — required only when capitalize=false
  // (capitalized charges hit Inventory Control on the Dr side automatically)
  claimAccountId: '',
  claimAccountCode: '',
  claimAccountName: '',
  capitalize: true,
  allocationMethod: 'value'
};

// ===== Component =====
const ImportChargeTemplates = () => {
  const { activeCompany, userProfile } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('masterData', 'importChargeTemplates');

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [showModal, setShowModal] = useState(false);
  useEscapeClose(showModal, () => setShowModal(false));
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    templateName: '',
    isDefault: false,
    isActive: true,
    notes: ''
  });
  const [charges, setCharges] = useState([{ ...EMPTY_CHARGE }]);

  const fetchTemplates = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, `companies/${activeCompany.id}/importChargeTemplates`),
        orderBy('templateName')
      );
      const snap = await getDocs(q);
      setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCompany?.id]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // ----- Modal open/close helpers -----
  const handleAdd = () => {
    setEditing(null);
    setFormData({ templateName: '', isDefault: templates.length === 0, isActive: true, notes: '' });
    setCharges([{ ...EMPTY_CHARGE }]);
    setShowModal(true);
  };

  const handleEdit = (tpl) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setEditing(tpl);
    setFormData({
      templateName: tpl.templateName || '',
      isDefault: !!tpl.isDefault,
      isActive: tpl.isActive !== false,
      notes: tpl.notes || ''
    });
    setCharges(tpl.charges?.length ? tpl.charges.map(c => ({ ...EMPTY_CHARGE, ...c })) : [{ ...EMPTY_CHARGE }]);
    setShowModal(true);
  };

  const handleDuplicate = (tpl) => {
    setEditing(null);
    setFormData({
      templateName: `${tpl.templateName} (Copy)`,
      isDefault: false,
      isActive: true,
      notes: tpl.notes || ''
    });
    setCharges(tpl.charges?.length ? tpl.charges.map(c => ({ ...EMPTY_CHARGE, ...c })) : [{ ...EMPTY_CHARGE }]);
    setShowModal(true);
  };

  // ----- Charge row handlers -----
  const updateCharge = (index, patch) => {
    setCharges(prev => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  const addCharge = () => setCharges(prev => [...prev, { ...EMPTY_CHARGE }]);
  const removeCharge = (index) => setCharges(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);

  const handleAccountChange = (index, account) => {
    updateCharge(index, {
      accountId: account?.id || '',
      accountCode: account?.code || '',
      accountName: account?.name || ''
    });
  };

  const handleClaimAccountChange = (index, account) => {
    updateCharge(index, {
      claimAccountId: account?.id || '',
      claimAccountCode: account?.code || '',
      claimAccountName: account?.name || ''
    });
  };

  // ----- Validation -----
  const validate = () => {
    if (!formData.templateName.trim()) return 'Template name is required.';
    const dupName = templates.find(t =>
      t.id !== editing?.id &&
      (t.templateName || '').trim().toLowerCase() === formData.templateName.trim().toLowerCase()
    );
    if (dupName) return 'A template with this name already exists.';
    if (charges.length === 0) return 'Add at least one charge row.';
    for (let i = 0; i < charges.length; i++) {
      const c = charges[i];
      if (!c.chargeName?.trim()) return `Row ${i + 1}: charge name is required.`;
      if (!c.accountId) return `Row ${i + 1}: select a Payable account.`;
      if (!c.capitalize && !c.claimAccountId) {
        return `Row ${i + 1}: non-capitalized charges need a Claim account (the input/receivable, e.g. GST Input, WHT Receivable).`;
      }
      if (Number(c.value) < 0) return `Row ${i + 1}: value cannot be negative.`;
      if (c.type === 'percentage' && Number(c.value) > 100) {
        // warn but don't block — handled via confirm below
      }
    }
    return null;
  };

  // ----- Save -----
  const handleSave = async () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    const err = validate();
    if (err) { alert(err); return; }

    // Soft-warn: any % charge > 100
    const hasOverHundred = charges.some(c => c.type === 'percentage' && Number(c.value) > 100);
    if (hasOverHundred && !window.confirm('One or more percentage charges are above 100%. Save anyway?')) return;

    setSaving(true);
    try {
      const cleanedCharges = charges.map(c => ({
        chargeName: c.chargeName.trim(),
        type: c.type,
        value: Number(c.value) || 0,
        base: c.type === 'percentage' ? c.base : 'manual',
        accountId: c.accountId,
        accountCode: c.accountCode,
        accountName: c.accountName,
        claimAccountId: c.capitalize ? '' : c.claimAccountId,
        claimAccountCode: c.capitalize ? '' : c.claimAccountCode,
        claimAccountName: c.capitalize ? '' : c.claimAccountName,
        capitalize: !!c.capitalize,
        allocationMethod: c.capitalize ? c.allocationMethod : 'manual'
      }));

      const payload = {
        templateName: formData.templateName.trim(),
        isDefault: !!formData.isDefault,
        isActive: !!formData.isActive,
        notes: formData.notes?.trim() || '',
        charges: cleanedCharges,
        chargeCount: cleanedCharges.length,
        updatedAt: serverTimestamp(),
        updatedBy: userProfile?.uid || null
      };

      const colRef = collection(db, `companies/${activeCompany.id}/importChargeTemplates`);

      // If marking as default, unset default on all other templates
      if (formData.isDefault) {
        const batch = writeBatch(db);
        templates.forEach(t => {
          if (t.id !== editing?.id && t.isDefault) {
            batch.update(doc(colRef, t.id), { isDefault: false, updatedAt: serverTimestamp() });
          }
        });
        await batch.commit();
      }

      if (editing) {
        await updateDoc(doc(colRef, editing.id), payload);
      } else {
        payload.createdAt = serverTimestamp();
        payload.createdBy = userProfile?.uid || null;
        await addDoc(colRef, payload);
      }

      setShowModal(false);
      fetchTemplates();
    } catch (err) {
      console.error('Error saving template:', err);
      alert('Failed to save template. ' + (err?.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tpl) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete template "${tpl.templateName}"?`)) return;
    try {
      await deleteDoc(doc(db, `companies/${activeCompany.id}/importChargeTemplates`, tpl.id));
      fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      alert('Failed to delete template.');
    }
  };

  // Keyboard shortcuts
  useDocumentShortcuts({
    onNew: handleAdd,
    onSave: handleSave,
    viewMode: showModal ? 'form' : 'list',
    saving
  });

  // ----- Filter -----
  const filtered = templates.filter(t => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (t.templateName || '').toLowerCase().includes(q) ||
           (t.notes || '').toLowerCase().includes(q);
  });

  // ===== Render =====
  return (
    <PageLayout pageTitle="Import Charge Templates" breadcrumb="Master Data / Import Charge Templates">
      <Container>
        <HeaderRow>
          <SearchBox>
            <FiSearch />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && <ClearBtn onClick={() => setSearchTerm('')}><FiX /></ClearBtn>}
          </SearchBox>
          {canEdit && (
            <AddButton onClick={handleAdd}>
              <FiPlus /> New Template
            </AddButton>
          )}
        </HeaderRow>

        {loading ? (
          <Loading>Loading templates...</Loading>
        ) : filtered.length === 0 ? (
          <Empty>
            <EmptyIcon><FiPackage /></EmptyIcon>
            <EmptyText>
              {searchTerm
                ? 'No templates match your search.'
                : 'No import charge templates yet. Create one to reuse the same duties + freight + clearing charges across import bills.'}
            </EmptyText>
            {canEdit && !searchTerm && (
              <AddButton onClick={handleAdd}><FiPlus /> Create First Template</AddButton>
            )}
          </Empty>
        ) : (
          <TableCard>
            <Table>
              <thead>
                <tr>
                  <Th>Template Name</Th>
                  <Th style={{ textAlign: 'center' }}># Charges</Th>
                  <Th style={{ textAlign: 'center' }}>Default</Th>
                  <Th style={{ textAlign: 'center' }}>Active</Th>
                  <Th>Notes</Th>
                  <Th style={{ textAlign: 'right' }}>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(tpl => (
                  <Tr key={tpl.id}>
                    <Td><strong>{tpl.templateName}</strong></Td>
                    <Td style={{ textAlign: 'center' }}>{tpl.chargeCount ?? tpl.charges?.length ?? 0}</Td>
                    <Td style={{ textAlign: 'center' }}>
                      {tpl.isDefault ? <DefaultBadge><FiStar size={12} /> Default</DefaultBadge> : '-'}
                    </Td>
                    <Td style={{ textAlign: 'center' }}>
                      <ActiveBadge $active={tpl.isActive !== false}>
                        {tpl.isActive !== false ? 'Active' : 'Inactive'}
                      </ActiveBadge>
                    </Td>
                    <Td style={{ color: colors.text.secondary, fontSize: '13px' }}>
                      {tpl.notes ? (tpl.notes.length > 60 ? tpl.notes.slice(0, 60) + '…' : tpl.notes) : '-'}
                    </Td>
                    <Td style={{ textAlign: 'right' }}>
                      <ActionGroup>
                        {canEdit && (
                          <>
                            <IconBtn onClick={() => handleEdit(tpl)} title="Edit"><FiEdit2 /></IconBtn>
                            <IconBtn onClick={() => handleDuplicate(tpl)} title="Duplicate"><FiCopy /></IconBtn>
                          </>
                        )}
                        {canDelete && (
                          <IconBtn $danger onClick={() => handleDelete(tpl)} title="Delete"><FiTrash2 /></IconBtn>
                        )}
                      </ActionGroup>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableCard>
        )}

        {/* ===== MODAL ===== */}
        {showModal && (
          <ModalOverlay onClick={() => setShowModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{editing ? 'Edit Template' : 'New Import Charge Template'}</ModalTitle>
                <CloseBtn onClick={() => setShowModal(false)}><FiX /></CloseBtn>
              </ModalHeader>

              <ModalBody>
                {/* Template metadata */}
                <FormSection>
                  <FormRow>
                    <FormGroup style={{ flex: 2 }}>
                      <Label>Template Name *</Label>
                      <Input
                        value={formData.templateName}
                        onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
                        placeholder="e.g. Standard Commercial Import"
                        autoFocus
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>&nbsp;</Label>
                      <CheckboxRow>
                        <input
                          type="checkbox"
                          id="isDefault"
                          checked={formData.isDefault}
                          onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                        />
                        <label htmlFor="isDefault">Set as default</label>
                      </CheckboxRow>
                      <CheckboxRow>
                        <input
                          type="checkbox"
                          id="isActive"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        />
                        <label htmlFor="isActive">Active</label>
                      </CheckboxRow>
                    </FormGroup>
                  </FormRow>
                  <FormGroup>
                    <Label>Notes</Label>
                    <Input
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="When to use this template (optional)"
                    />
                  </FormGroup>
                </FormSection>

                {/* Charges Table */}
                <SectionHeader>
                  <SectionTitle>Import Charges</SectionTitle>
                  <SmallAddBtn onClick={addCharge}><FiPlus /> Add Charge</SmallAddBtn>
                </SectionHeader>

                <ChargeTableWrapper>
                  <ChargeTable>
                    <thead>
                      <tr>
                        <ChTh style={{ width: 36 }}>#</ChTh>
                        <ChTh style={{ minWidth: 160 }}>Charge Name</ChTh>
                        <ChTh style={{ width: 110 }}>Type</ChTh>
                        <ChTh style={{ width: 110 }}>Value</ChTh>
                        <ChTh style={{ width: 160 }}>Base</ChTh>
                        <ChTh style={{ minWidth: 220 }}>Payable Account (Cr)</ChTh>
                        <ChTh style={{ width: 100, textAlign: 'center' }}>Capitalize</ChTh>
                        <ChTh style={{ minWidth: 220 }}>Claim Account (Dr)</ChTh>
                        <ChTh style={{ width: 140 }}>Allocation</ChTh>
                        <ChTh style={{ width: 40 }}></ChTh>
                      </tr>
                    </thead>
                    <tbody>
                      {charges.map((c, i) => (
                        <ChTr key={i}>
                          <ChTd>{i + 1}</ChTd>
                          <ChTd>
                            <ChInput
                              value={c.chargeName}
                              onChange={(e) => updateCharge(i, { chargeName: e.target.value })}
                              placeholder="e.g. Customs Duty"
                            />
                          </ChTd>
                          <ChTd>
                            <ChSelect
                              value={c.type}
                              onChange={(e) => updateCharge(i, { type: e.target.value })}
                            >
                              {CHARGE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </ChSelect>
                          </ChTd>
                          <ChTd>
                            <ChInput
                              type="number"
                              step="0.01"
                              min="0"
                              value={c.value}
                              onChange={(e) => updateCharge(i, { value: e.target.value })}
                              style={{ textAlign: 'right' }}
                            />
                          </ChTd>
                          <ChTd>
                            <ChSelect
                              value={c.base}
                              onChange={(e) => updateCharge(i, { base: e.target.value })}
                              disabled={c.type !== 'percentage'}
                            >
                              {BASE_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                            </ChSelect>
                          </ChTd>
                          <ChTd>
                            <AccountSelect
                              companyId={activeCompany?.id}
                              value={c.accountId}
                              onChange={(account) => handleAccountChange(i, account)}
                              label="Payable Account"
                            />
                          </ChTd>
                          <ChTd style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={c.capitalize}
                              onChange={(e) => updateCharge(i, { capitalize: e.target.checked })}
                              title="If checked, this charge adds to inventory cost (Dr Inventory). If unchecked, you must pick a Claim Account."
                            />
                          </ChTd>
                          <ChTd>
                            {c.capitalize ? (
                              <DisabledHint>Inventory Control (auto)</DisabledHint>
                            ) : (
                              <AccountSelect
                                companyId={activeCompany?.id}
                                value={c.claimAccountId}
                                onChange={(account) => handleClaimAccountChange(i, account)}
                                label="Claim Account"
                              />
                            )}
                          </ChTd>
                          <ChTd>
                            <ChSelect
                              value={c.allocationMethod}
                              onChange={(e) => updateCharge(i, { allocationMethod: e.target.value })}
                              disabled={!c.capitalize}
                            >
                              {ALLOCATION_METHODS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                            </ChSelect>
                          </ChTd>
                          <ChTd style={{ textAlign: 'center' }}>
                            <RemoveBtn onClick={() => removeCharge(i)} disabled={charges.length === 1}>
                              <FiX />
                            </RemoveBtn>
                          </ChTd>
                        </ChTr>
                      ))}
                    </tbody>
                  </ChargeTable>
                </ChargeTableWrapper>

                <HelperNote>
                  <strong>Payable Account (Cr)</strong> — always required. The liability/credit side: who you owe (Customs Payable, Freight Payable, Bank if paid).<br />
                  <strong>Claim Account (Dr)</strong> — required only when <em>Capitalize</em> is OFF. The input/receivable side you'll claim later (GST Input, WHT Receivable). When <em>Capitalize</em> is ON, the Dr side automatically flows to Inventory Control via landed cost.<br />
                  <strong>Base</strong> applies only to % charges. <em>CIF + Prior Charges</em> = cumulative — the row sees CIF plus the total of every charge listed above it. Order rows accordingly: duties first, then dependent taxes, then freight/clearing.
                </HelperNote>
              </ModalBody>

              <ModalFooter>
                <CancelBtn onClick={() => setShowModal(false)}>Cancel</CancelBtn>
                <SaveBtn onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : (editing ? 'Update Template' : 'Save Template')}
                </SaveBtn>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}
      </Container>
    </PageLayout>
  );
};

// ===== Styles =====
const Container = styled.div`padding: ${spacing.lg};`;

const HeaderRow = styled.div`
  display: flex; gap: ${spacing.md}; align-items: center;
  margin-bottom: ${spacing.lg}; flex-wrap: wrap;
`;

const SearchBox = styled.div`
  flex: 1; min-width: 240px;
  display: flex; align-items: center; gap: ${spacing.sm};
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.background.default};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  color: ${colors.text.secondary};

  input {
    flex: 1; border: none; outline: none; background: transparent;
    font-size: ${typography.fontSize.sm}; color: ${colors.text.primary};
  }
`;

const ClearBtn = styled.button`
  background: ${colors.neutral[200]}; border: none; cursor: pointer;
  width: 22px; height: 22px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: ${colors.text.secondary};
  &:hover { background: ${colors.error.main}; color: white; }
`;

const AddButton = styled.button`
  display: flex; align-items: center; gap: ${spacing.sm};
  padding: ${spacing.sm} ${spacing.lg};
  background: ${colors.primary.main}; color: white;
  border: none; border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm}; font-weight: ${typography.fontWeight.semibold};
  cursor: pointer; transition: background 0.2s;
  &:hover { background: ${colors.primary.dark}; }
`;

const Loading = styled.div`
  padding: ${spacing.xxl}; text-align: center;
  color: ${colors.text.secondary}; font-size: ${typography.fontSize.md};
`;

const Empty = styled.div`
  padding: ${spacing.xxl}; text-align: center;
  background: ${colors.background.default};
  border-radius: ${borderRadius.lg};
  display: flex; flex-direction: column; align-items: center; gap: ${spacing.md};
`;

const EmptyIcon = styled.div`
  width: 80px; height: 80px; border-radius: 50%;
  background: ${colors.primary.main}15; color: ${colors.primary.main};
  display: flex; align-items: center; justify-content: center;
  font-size: 36px;
`;

const EmptyText = styled.p`
  color: ${colors.text.secondary}; max-width: 480px;
  font-size: ${typography.fontSize.md}; line-height: 1.6;
`;

const TableCard = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.lg};
  box-shadow: ${shadows.sm};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%; border-collapse: collapse;
`;

const Th = styled.th`
  padding: ${spacing.md}; text-align: left;
  background: ${colors.neutral[50]};
  color: ${colors.text.secondary};
  font-size: 12px; font-weight: ${typography.fontWeight.semibold};
  text-transform: uppercase; letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.border.light};
`;

const Tr = styled.tr`
  &:hover { background: ${colors.neutral[50]}; }
`;

const Td = styled.td`
  padding: ${spacing.md};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.primary};
  border-bottom: 1px solid ${colors.neutral[100]};
`;

const DefaultBadge = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px; border-radius: ${borderRadius.xl};
  background: ${colors.warning.main}20; color: ${colors.warning.dark};
  font-size: 11px; font-weight: ${typography.fontWeight.semibold};
`;

const ActiveBadge = styled.span`
  padding: 3px 10px; border-radius: ${borderRadius.xl};
  background: ${props => props.$active ? colors.success.main + '20' : colors.neutral[200]};
  color: ${props => props.$active ? colors.success.dark : colors.text.secondary};
  font-size: 11px; font-weight: ${typography.fontWeight.semibold};
`;

const ActionGroup = styled.div`
  display: inline-flex; gap: ${spacing.xs};
`;

const IconBtn = styled.button`
  width: 30px; height: 30px;
  display: flex; align-items: center; justify-content: center;
  background: transparent; border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm}; cursor: pointer;
  color: ${props => props.$danger ? colors.error.main : colors.text.secondary};
  transition: all 0.2s;
  &:hover {
    background: ${props => props.$danger ? colors.error.main : colors.primary.main};
    color: white; border-color: transparent;
  }
`;

// Modal
const ModalOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: ${spacing.md};
`;

const ModalContent = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.lg};
  width: 100%; max-width: 1420px;
  max-height: 92vh; display: flex; flex-direction: column;
  box-shadow: ${shadows.xl};
`;

const ModalHeader = styled.div`
  padding: ${spacing.lg};
  border-bottom: 1px solid ${colors.border.light};
  display: flex; align-items: center; justify-content: space-between;
`;

const ModalTitle = styled.h2`
  font-size: ${typography.fontSize.xl}; color: ${colors.text.primary};
`;

const CloseBtn = styled.button`
  width: 32px; height: 32px; border: none; background: transparent;
  cursor: pointer; color: ${colors.text.secondary};
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%;
  &:hover { background: ${colors.neutral[200]}; }
`;

const ModalBody = styled.div`
  padding: ${spacing.lg}; overflow-y: auto; flex: 1;
`;

const FormSection = styled.div`margin-bottom: ${spacing.lg};`;

const FormRow = styled.div`
  display: flex; gap: ${spacing.md}; margin-bottom: ${spacing.md}; flex-wrap: wrap;
`;

const FormGroup = styled.div`
  display: flex; flex-direction: column; gap: ${spacing.xs}; flex: 1; min-width: 200px;
`;

const Label = styled.label`
  font-size: ${typography.fontSize.sm}; font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.secondary};
`;

const Input = styled.input`
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.primary};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const CheckboxRow = styled.div`
  display: flex; align-items: center; gap: ${spacing.sm};
  font-size: ${typography.fontSize.sm}; color: ${colors.text.primary};
  padding: 4px 0;
  input { cursor: pointer; }
  label { cursor: pointer; }
`;

const SectionHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin: ${spacing.lg} 0 ${spacing.sm};
`;

const SectionTitle = styled.h4`
  font-size: ${typography.fontSize.md}; color: ${colors.text.primary};
`;

const SmallAddBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  padding: 6px ${spacing.md};
  background: ${colors.primary.main}; color: white;
  border: none; border-radius: ${borderRadius.sm};
  font-size: 12px; font-weight: ${typography.fontWeight.semibold};
  cursor: pointer;
  &:hover { background: ${colors.primary.dark}; }
`;

const ChargeTableWrapper = styled.div`
  /* overflow MUST be visible — otherwise the AccountSelect dropdown gets clipped
     when it opens. Modal width is wide enough (1420px) to fit the table without
     needing horizontal scroll. */
  overflow: visible;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
`;

const ChargeTable = styled.table`
  width: 100%; border-collapse: collapse;
  min-width: 1280px;
`;

const DisabledHint = styled.div`
  padding: 6px ${spacing.sm};
  font-size: 12px; font-style: italic;
  color: ${colors.text.tertiary};
  background: ${colors.neutral[100]};
  border: 1px dashed ${colors.border.light};
  border-radius: ${borderRadius.sm};
  text-align: center;
`;

const ChTh = styled.th`
  padding: 8px ${spacing.sm};
  background: ${colors.neutral[50]};
  color: ${colors.text.secondary};
  font-size: 11px; font-weight: ${typography.fontWeight.semibold};
  text-transform: uppercase; text-align: left;
  border-bottom: 1px solid ${colors.border.light};
  white-space: nowrap;
`;

const ChTr = styled.tr`
  &:not(:last-child) { border-bottom: 1px solid ${colors.neutral[100]}; }
`;

const ChTd = styled.td`
  padding: 6px ${spacing.sm};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.primary};
  vertical-align: middle;
`;

const ChInput = styled.input`
  width: 100%; padding: 6px ${spacing.sm};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.primary};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const ChSelect = styled.select`
  width: 100%; padding: 6px ${spacing.sm};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.primary};
  background: ${colors.background.default};
  &:disabled { background: ${colors.neutral[100]}; color: ${colors.text.tertiary}; cursor: not-allowed; }
`;

const RemoveBtn = styled.button`
  width: 28px; height: 28px;
  background: transparent; border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  display: flex; align-items: center; justify-content: center;
  color: ${colors.error.main}; cursor: pointer;
  &:hover:not(:disabled) { background: ${colors.error.main}; color: white; border-color: transparent; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const HelperNote = styled.p`
  margin-top: ${spacing.md};
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.info.main}10;
  border-left: 3px solid ${colors.info.main};
  border-radius: ${borderRadius.sm};
  font-size: 12px; color: ${colors.text.secondary}; line-height: 1.6;
`;

const ModalFooter = styled.div`
  padding: ${spacing.lg};
  border-top: 1px solid ${colors.border.light};
  display: flex; justify-content: flex-end; gap: ${spacing.md};
`;

const CancelBtn = styled.button`
  padding: ${spacing.sm} ${spacing.lg};
  background: transparent; color: ${colors.text.secondary};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm}; font-weight: ${typography.fontWeight.semibold};
  cursor: pointer;
  &:hover { background: ${colors.neutral[100]}; color: ${colors.text.primary}; }
`;

const SaveBtn = styled.button`
  padding: ${spacing.sm} ${spacing.xl};
  background: ${colors.primary.main}; color: white;
  border: none; border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm}; font-weight: ${typography.fontWeight.semibold};
  cursor: pointer;
  &:hover:not(:disabled) { background: ${colors.primary.dark}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export default ImportChargeTemplates;
